/*
 * CUSTOM (Dr. Brandt, Oct 2026): cart drawer behaviour.
 *  - <line-item-plan>        One-time / Subscribe toggle on line items (Skio selling plans)
 *  - <cart-discount-toggle>  "Have a discount code?" link that reveals Prestige's discount field
 *  - <cart-upsell-autoplay>  Optional, gentle auto-advance for the "Complete the routine" carousel
 *
 * Plain script (not a module) so it can load independently of theme.js. Every element is defined once.
 */
(() => {
  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const html = document.documentElement;

  /* ---------------------------------------------------------------------------------------------------------------
     ONE-TIME / SUBSCRIBE TOGGLE
     /cart/change.js only accepts `line` (1-based index) when changing a selling plan. selling_plan: null removes it.
     --------------------------------------------------------------------------------------------------------------- */
  class LineItemPlan extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    async onClick(event) {
      const button = event.target.closest('[data-selling-plan]');

      if (!button || button.getAttribute('aria-pressed') === 'true' || this.getAttribute('aria-busy') === 'true') {
        return;
      }

      const sellingPlan = button.getAttribute('data-selling-plan');
      const sections = [];

      this.setAttribute('aria-busy', 'true');
      this.querySelector('[role="alert"]')?.remove();
      html.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      html.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      try {
        const response = await fetch(`${routesRoot()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(this.getAttribute('line'), 10),
            quantity: parseInt(this.getAttribute('quantity'), 10),
            selling_plan: sellingPlan ? parseInt(sellingPlan, 10) : null,
            sections: sections.join(',')
          })
        });

        const cart = await response.json();

        if (!response.ok) {
          throw new Error(cart.description || cart.message || 'Something went wrong. Please try again.');
        }

        if (window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
          return;
        }

        html.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart }
        }));
      } catch (error) {
        this.removeAttribute('aria-busy');

        const alert = document.createElement('p');
        alert.className = 'line-item-plan__error text-xs';
        alert.setAttribute('role', 'alert');
        alert.textContent = error.message;
        this.appendChild(alert);

        // Re-sync the drawer with the real cart in case the change partially applied
        html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } finally {
        html.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     DISCOUNT CODE TOGGLE
     Applying still goes through Prestige's <cart-discount-field>, which applies on the input's "change" event.
     --------------------------------------------------------------------------------------------------------------- */
  class CartDiscountToggle extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.lastSubmittedValue = null;
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('change', this.onChange, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('change', this.onChange, true);
    }

    get trigger() {
      return this.querySelector('.cart-drawer__discount-trigger');
    }

    get panel() {
      return this.querySelector('.cart-drawer__discount-panel');
    }

    get input() {
      return this.querySelector('.cart-discount-field input:not([type="hidden"])');
    }

    open() {
      if (!this.panel) {
        return;
      }

      this.panel.hidden = false;

      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'true');
        this.trigger.hidden = true;
      }

      this.input?.focus();
    }

    onChange(event) {
      if (event.target === this.input) {
        this.lastSubmittedValue = event.target.value.trim();
      }
    }

    onClick(event) {
      if (event.target.closest('.cart-drawer__discount-trigger')) {
        this.open();
        return;
      }

      if (event.target.closest('.cart-discount-field__apply')) {
        const input = this.input;

        if (!input || input.value.trim() === '') {
          input?.focus();
          return;
        }

        // Tapping Apply usually blurs the input first, which already fired "change". Only fire it if it didn't.
        if (input.value.trim() !== this.lastSubmittedValue) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    onKeyDown(event) {
      // Enter inside the code field would otherwise submit the drawer form (to /cart)
      if (event.key === 'Enter' && event.target === this.input) {
        event.preventDefault();
        event.target.blur();
      }
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     UPSELL CAROUSEL AUTO-ADVANCE (off by default; theme editor > Cart drawer > "Auto-advance the carousel")
     Runs only while the drawer is open, goes through the products once, and stops for good on any interaction.
     --------------------------------------------------------------------------------------------------------------- */
  class CartUpsellAutoplay extends HTMLElement {
    constructor() {
      super();
      this.stop = this.stop.bind(this);
      this.timer = null;
    }

    connectedCallback() {
      const seconds = parseFloat(this.getAttribute('interval'));

      if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach((type) => {
        this.addEventListener(type, this.stop, { passive: true });
      });

      this.timer = window.setInterval(() => this.tick(), seconds * 1000);
    }

    disconnectedCallback() {
      this.stop();
    }

    tick() {
      const drawer = this.closest('cart-drawer');
      const carousel = this.querySelector('scroll-carousel');

      if (document.hidden || (drawer && !drawer.hasAttribute('open')) || !carousel || !carousel.cells) {
        return;
      }

      if (carousel.selectedIndex >= carousel.cells.length - 1) {
        this.stop();
        return;
      }

      carousel.next();
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     QUANTITY -/+ AND TRASH IN THE CART DRAWER
     The store's auto-add / free-gift app rewrites these controls (href="javascript:void(0)" + aca-line / aca-quantity)
     and runs its own handler, which sends shoppers to the cart page. Its line numbers and quantities also go stale as
     soon as the cart changes. So we ignore its attributes completely: every <line-item> carries its own Shopify line
     key and current quantity (data-dbr-*, written by the theme on every redraw), and we work out minus / plus / remove
     from which control was clicked. The click is caught on window in the capture phase, before the app or the link
     itself sees it. The app still sees the change, since it watches the store's cart requests.
     --------------------------------------------------------------------------------------------------------------- */
  let quantityChangeInFlight = false;

  window.addEventListener('click', async (event) => {
    const control = event.target instanceof Element
      ? event.target.closest('cart-drawer line-item :is(.quantity-selector__button, .line-item__remove)')
      : null;

    if (!control || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineItem = control.closest('line-item');
    const lineKey = lineItem?.getAttribute('data-dbr-line-key');
    const currentQuantity = parseInt(lineItem?.getAttribute('data-dbr-quantity'), 10);

    if (quantityChangeInFlight || !lineKey || isNaN(currentQuantity) || control.hasAttribute('disabled')) {
      return;
    }

    let quantity;

    if (control.classList.contains('line-item__remove')) {
      quantity = 0;
    } else {
      const buttons = Array.from(control.closest('quantity-selector')?.querySelectorAll('.quantity-selector__button') || []);
      const isDecrease = buttons.indexOf(control) === 0;
      const step = parseInt(lineItem.querySelector('.quantity-selector__input')?.getAttribute('step'), 10) || 1;
      quantity = Math.max(0, currentQuantity + (isDecrease ? -step : step));
    }

    const sections = [];
    html.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    quantityChangeInFlight = true;
    lineItem.setAttribute('aria-busy', 'true');
    lineItem.querySelector('.line-item__error')?.remove();
    html.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    // Never let a hung request lock the controls
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(`${routesRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity, sections: sections.join(',') }),
        signal: controller.signal
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Something went wrong. Please try again.');
      }

      html.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart }
      }));

      // The gift app may add or remove its free item right after a change, without telling the drawer.
      // Pull the real cart once more shortly after, so the drawer shows it.
      window.clearTimeout(window.__dbrCartResyncTimer);
      window.__dbrCartResyncTimer = window.setTimeout(() => {
        html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }, 1500);
    } catch (error) {
      if (lineItem.isConnected) {
        lineItem.removeAttribute('aria-busy');

        const alert = document.createElement('p');
        alert.className = 'line-item__error line-item-plan__error text-xs';
        alert.setAttribute('role', 'alert');
        alert.textContent = error.name === 'AbortError' ? 'That took too long. Please try again.' : error.message;
        lineItem.appendChild(alert);
      }

      // Re-sync the drawer with the real cart (e.g. if the gift app changed it in the meantime)
      html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      window.clearTimeout(timeout);
      quantityChangeInFlight = false;
      html.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }, true);

  if (!window.customElements.get('line-item-plan')) {
    window.customElements.define('line-item-plan', LineItemPlan);
  }

  if (!window.customElements.get('cart-discount-toggle')) {
    window.customElements.define('cart-discount-toggle', CartDiscountToggle);
  }

  if (!window.customElements.get('cart-upsell-autoplay')) {
    window.customElements.define('cart-upsell-autoplay', CartUpsellAutoplay);
  }
})();
